import React from 'react';
import { useTranslation } from 'react-i18next';

import GoogleMapsAddressInput from '@/components/GoogleMapsAddressInput';
import { isGoogleMapsJsConfigured } from '@/lib/checkout/deliveryAddress';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export default function CheckoutDeliverySection({
  deliverySectionRef,
  useAddressZonesForDelivery,
  address,
  setAddress,
  markerPosition,
  setMarkerPosition,
  chatCtx,
  deliveryQuote,
  deliveryLoading,
  geocodeLoading,
  solugoCoverage,
  solugoCoverageLoading,
  showSoluGoCoverageHint,
}) {
  const { t } = useTranslation();
  const mapsJsConfigured = isGoogleMapsJsConfigured();
  return (
    <Card className="p-6" ref={deliverySectionRef} id="checkout-delivery-section">
      <h2 className="text-xl font-semibold mb-1">{t('checkout.deliveryTitle')}</h2>
      <p className="text-sm text-muted-foreground mb-4">
        {useAddressZonesForDelivery
          ? t('checkout.deliveryHint')
          : !String(address || '').trim()
            ? chatCtx?.mood === 'frustrated'
              ? t('checkout.deliveryNeedPin')
              : t('checkout.deliveryNeedAddress')
            : geocodeLoading
              ? t('checkout.deliveryLocating')
              : t('checkout.deliverySavedHint')}
      </p>
      {!mapsJsConfigured && !useAddressZonesForDelivery ? (
        <p className="text-xs text-muted-foreground mb-3">
          El mapa puede no cargar en este dispositivo; igual ubicamos tu dirección con el texto que escribas.
        </p>
      ) : null}
      {String(address || '').trim() ? (
        <div className="mb-4 rounded-lg border bg-background/80 px-4 py-3 text-sm">
          <span className="font-medium text-foreground">Dirección: </span>
          <span className="text-muted-foreground">{String(address).trim()}</span>
          <div className="mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
              onClick={() =>
                deliverySectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
              }
            >
              Ajustar dirección en el mapa
            </Button>
          </div>
        </div>
      ) : null}
      <div className="space-y-4">
        {showSoluGoCoverageHint && solugoCoverageLoading ? (
          <p className="text-xs text-muted-foreground">Verificando cobertura de delivery…</p>
        ) : null}
        {showSoluGoCoverageHint && solugoCoverage?.unavailable ? (
          <p className="text-xs text-muted-foreground">
            {solugoCoverage.reason ||
              'No se pudo verificar cobertura SoluGo en este momento. Podés continuar; el local confirmará si hay delivery.'}
          </p>
        ) : null}
        {showSoluGoCoverageHint &&
        solugoCoverage &&
        !solugoCoverage.unavailable &&
        solugoCoverage.inCoverage === false ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            <p className="font-medium">Delivery logístico no disponible en esta zona</p>
            <p className="mt-1 text-amber-900/90 dark:text-amber-100/90">
              {solugoCoverage.reason ||
                'Esta dirección está fuera de la cobertura operativa actual. Podés contactar al local para confirmar alternativas.'}
            </p>
          </div>
        ) : null}
        <GoogleMapsAddressInput
          address={address}
          setAddress={setAddress}
          location={markerPosition}
          setLocation={setMarkerPosition}
          label="Dirección Completa"
          placeholder="Calle, número, depto, referencias..."
          mapHint={
            useAddressZonesForDelivery
              ? 'Opcional: podés mover el mapa o el pin para referencia. El costo se calcula con la dirección y las zonas del local.'
              : 'Arrastra el marcador rojo para ajustar tu ubicación de entrega'
          }
          height="250px"
        />

        {useAddressZonesForDelivery
          ? String(address || '').trim() && (
              <div className="p-4 bg-secondary rounded-lg space-y-1">
                {deliveryQuote?.no_zone_match ? (
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    No tenemos cobertura para esta zona
                  </p>
                ) : typeof deliveryQuote?.delivery_fee === 'number' ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{t('checkout.deliveryFeeLabel')}</span>
                      <span className="text-xl font-bold">
                        ${deliveryQuote.delivery_fee.toLocaleString('es-CL')}
                      </span>
                    </div>
                    {typeof deliveryQuote.estimated_minutes === 'number' &&
                    deliveryQuote.estimated_minutes > 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t('checkout.etaApprox', { n: deliveryQuote.estimated_minutes })}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </div>
            )
          : (geocodeLoading ||
              deliveryLoading ||
              typeof deliveryQuote?.delivery_fee === 'number' ||
              markerPosition) && (
              <div className="p-4 bg-secondary rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{t('checkout.deliveryCostLabel')}</span>
                  <span className="text-xl font-bold">
                    {geocodeLoading
                      ? 'Ubicando dirección...'
                      : deliveryLoading
                        ? 'Calculando...'
                        : typeof deliveryQuote?.delivery_fee === 'number'
                          ? `$${deliveryQuote.delivery_fee.toLocaleString()}`
                          : markerPosition
                            ? 'Por calcular'
                            : '—'}
                  </span>
                </div>
              </div>
            )}
      </div>
    </Card>
  );
}
